// ── QC Inspection Report — single print document ─────────────────────────────
// Page 1: designed cover image (public/qc-cover-mozi.jpg) with the report details ·
// Page 2: inspection form · Page 3+: QC photo sections. Pages 2+ follow the MOZI
// theme: warm paper, walnut / tan section bars, brand header.
// Everything comes from the saved report + app settings; empty fields print as
// blank form lines (never invented values). Paginated by paged.js so the whole
// report is ONE print job with running header/footer and "Page X / Y".
import { buildImageUrl } from '../../utils/imageUrl';

const THEME = {
  ink: '#2b2a27',      // charcoal text
  logo: '#4a4845',     // MOZI wordmark grey
  walnut: '#9a5c2e',   // section number boxes
  rust: '#a8432a',     // photo section numbers / rules
  gold: '#b08a4f',
  tan: '#ece3d6',      // section bars / panel heads
  tanLine: '#ddd1bf',
  muted: '#7a746c',
  line: '#e2dbd1',
  soft: '#f6f1ea',
  paper: '#fbf8f3',
  pass: '#3b8a4f',
  fail: '#b3362b',
  na: '#8d877e',
};

const LEVEL_LABELS = { final: 'Final Inspection', white_body: 'White Body Inspection' };
const RESULT_OPTIONS = [['approved', 'Approved', 'pass'], ['rework', 'Rework Required', 'rework'], ['rejected', 'Rejected', 'fail']];
const BRAND_TAGLINE = 'Furniture | Spaces | Solutions';
import { pointsFor } from '../../utils/qcGuide';

const FOOTER_TAGLINE = 'Consistent Quality | Precision Manufacturing | Reliable Spaces';


function esc(v) {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
// For CSS content:"..." strings — HTML entities are not decoded inside <style>
function cssStr(v) {
  return String(v ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/</g, '\\3C ')
    .replace(/[\r\n]+/g, ' ');
}
const has = v => v != null && String(v).trim() !== '';
function fmtDate(v) {
  if (!has(v)) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtQty(v, unit) {
  if (!has(v)) return '';
  const n = Number(v);
  const s = Number.isInteger(n) ? String(n) : String(+n.toFixed(2));
  return unit ? `${s} ${unit}` : s;
}

// Filled value, or a blank write-in line when there is no data
const field = (v, wide) => has(v) ? `<span class="fv">${esc(v)}</span>` : `<span class="blank${wide ? ' wide' : ''}"></span>`;

// Print-safe checkbox (inline SVG renders identically on screen, print and PDF).
// Marked boxes are filled (green pass / red fail / grey N/A / walnut rework).
const TICK = '<path d="M3.4 8.3l3 3 6.2-6.6" fill="none" stroke="#fff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>';
function box(state) {
  const mark = {
    pass: `<svg viewBox="0 0 16 16">${TICK}</svg>`,
    rework: `<svg viewBox="0 0 16 16">${TICK}</svg>`,
    fail: '<svg viewBox="0 0 16 16"><path d="M4.3 4.3l7.4 7.4M11.7 4.3l-7.4 7.4" fill="none" stroke="#fff" stroke-width="2.3" stroke-linecap="round"/></svg>',
    na: '<svg viewBox="0 0 16 16"><path d="M4.2 8h7.6" fill="none" stroke="#fff" stroke-width="2.3" stroke-linecap="round"/></svg>',
  }[state] || '';
  return `<span class="cb${state ? ' cb-' + state : ''}">${mark}</span>`;
}
const pad2 = n => String(n).padStart(2, '0');

export function buildQcReportHtml(report, settings = {}, origin = window.location.origin) {
  const companyName = (settings.company_name || report.vendor_name || '').trim();
  const levelLabel = LEVEL_LABELS[report.inspection_level] || '';
  const projectName = report.project_name_live || '';
  const projectCode = report.project_code || '';
  // Wordmark in the page header: first word of the firm name (e.g. "MOZI")
  const brandWord = (companyName.split(/\s+/)[0] || '').toUpperCase();

  const moNo = report.mo_number || report.main_po_no || '';
  const finishCode = report.finish_code
    || (report.finish_sheet_codes || []).join(', ')
    || report.main_po_finishes_note || '';
  const orderQty = report.order_qty != null ? fmtQty(report.order_qty) : fmtQty(report.item_quantity, report.item_unit);
  const clientProject = [report.client_name, projectName].filter(has).join(' / ');
  const productName = [report.item_name, report.sku_number ? `SKU ${report.sku_number}` : ''].filter(has).join(' · ');

  const allImages = (report.sections || []).flatMap(s => s.images || []);
  const coverUrl = `${origin}/qc-cover-mozi.jpg`;

  // ── Page 1: cover ──────────────────────────────────────────────────────────
  // Full-page designed artwork; the details sit in the artwork's open space
  // under "FURNITURE MANUFACTURING" and only list fields that have data.
  const plate = [
    ['Report No.', report.report_no],
    ['Date', fmtDate(report.inspection_date)],
    // Name only; the code shows only when a project has no name
    ['Project', projectName || projectCode, 'wrap'],
    ['Product', [report.item_name, report.item_proto_code].filter(has).join(' · ')],
  ].filter(([, v]) => has(v));

  const cover = `
  <section class="cover">
    <img class="cv-art" src="${esc(coverUrl)}" alt="" data-cover="1" />
    <div class="cv-fallback">
      ${companyName ? `<div class="cvf-co">${esc(companyName)}</div>` : ''}
      <div class="cvf-title">QC <b>Report</b></div>
      ${levelLabel ? `<div class="cvf-sub">${esc(levelLabel)}</div>` : ''}
    </div>
    ${plate.length ? `<div class="cv-plate">
      ${plate.map(([k, v, wrap]) => `<div class="cv-row"><span class="cv-k">${esc(k)}</span><span class="cv-v${wrap ? ' cv-wrap' : ''}">${esc(v)}</span></div>`).join('')}
    </div>` : ''}
  </section>`;

  // Running page header (pages 2+): brand left, report reference right
  const runHeads = `
    <div class="runBrand">
      ${brandWord ? `<div class="rb-word">${esc(brandWord)}</div><div class="rb-tag">${esc(BRAND_TAGLINE)}</div>` : ''}
    </div>
    <div class="runDoc"><div>QC Inspection Report</div>${report.report_no ? `<b>${esc(report.report_no)}</b>` : ''}</div>`;

  const bar = (no, title, extra = '') => `<h2 class="bar${extra}"><span class="bar-no">${pad2(no)}</span><span class="bar-t">${title}</span></h2>`;

  // ── Page 2: inspection form ────────────────────────────────────────────────
  const infoRows = [
    [['QC Report No.', report.report_no], ['Inspection Date', fmtDate(report.inspection_date)]],
    [['MO No.', moNo], ['Client / Project', clientProject]],
    [['Product / Proto Code', report.item_proto_code], ['Product Name', productName]],
    [['Drawing / Revision', report.drawing_revision], ['Finish / Fabric Code', finishCode]],
  ];
  const qtyCells = [
    ['Order Qty', orderQty], ['Inspected Qty', fmtQty(report.inspected_qty)],
    ['Accepted', fmtQty(report.accepted_qty)], ['Rework', fmtQty(report.rework_qty)], ['Rejected', fmtQty(report.rejected_qty)],
  ];
  const infoHtml = infoRows.map(row => `<tr>${row.map(([k, v]) =>
    `<th>${esc(k)}</th><td>${field(v, true)}</td>`).join('')}</tr>`).join('') + `
    <tr class="qty"><td colspan="4"><div class="qrow">${qtyCells.map(([k, v]) =>
      `<span><span class="fk">${esc(k)}:</span> ${field(v)}</span>`).join('')}</div></td></tr>`;

  const checklist = report.checklist || [];
  const checkRows = checklist.map((c, i) => `
    <tr>
      <td class="c sn">${i + 1}</td>
      <td class="ip">${esc(c.label)}</td>
      <td>${esc(c.criteria)}</td>
      <td class="c">${box(c.result === 'pass' ? 'pass' : '')}</td>
      <td class="c">${box(c.result === 'fail' ? 'fail' : '')}</td>
      <td class="c">${box(c.result === 'na' ? 'na' : '')}</td>
      <td class="rm">${esc(c.remark || '')}</td>
    </tr>`).join('');
  const counts = { pass: 0, fail: 0, na: 0, open: 0 };
  checklist.forEach(c => { counts[c.result || 'open'] += 1; });

  const resultHtml = RESULT_OPTIONS.map(([k, label, st]) =>
    `<span class="opt${report.inspection_result === k ? ' opt-on opt-' + k : ''}">${box(report.inspection_result === k ? st : '')} ${esc(label)}</span>`).join('');

  const form = `
  <section class="form${allImages.length ? ' has-next' : ''}">
    ${runHeads}
    <header class="fh">
      <div class="fh-k">Quality Control (QC)</div>
      <div class="fh-t">Inspection Report</div>
      ${levelLabel ? `<div class="fh-k">${esc(levelLabel)}</div>` : ''}
    </header>

    ${bar(1, 'Order &amp; Product Information')}
    <table class="info">${infoHtml}</table>

    ${bar(2, 'Quality Inspection Checklist')}
    <table class="check">
      <thead><tr><th class="c sn">S.No.</th><th>Inspection Point</th><th>Acceptance Criteria</th><th class="c">Pass</th><th class="c">Fail</th><th class="c">N/A</th><th class="rm">Remarks</th></tr></thead>
      <tbody>${checkRows}</tbody>
    </table>
    <div class="tally">
      <span>${box('pass')} Pass <b>${counts.pass}</b></span>
      <span>${box('fail')} Fail <b>${counts.fail}</b></span>
      <span>${box('na')} N/A <b>${counts.na}</b></span>
      ${counts.open ? `<span class="open">Not inspected <b>${counts.open}</b></span>` : ''}
    </div>

    <div class="keep">
    ${bar(3, 'Final Disposition &amp; Approval')}
    <table class="disp">
      <tr><td><span class="fk">Inspection Result:</span> ${resultHtml}</td></tr>
      <tr><td><span class="fk">QC Remarks:</span> ${has(report.qc_remarks) ? `<span class="remarks">${esc(report.qc_remarks)}</span>` : '<span class="blank xwide"></span>'}</td></tr>
      <tr><td class="signs">
        <span><span class="fk">QC Inspector:</span> ${field(report.qc_person_name)}</span>
        <span><span class="fk">Production Head:</span> ${field(report.production_head)}</span>
        <span><span class="fk">Date:</span> ${field(fmtDate(report.signoff_date))}</span>
      </td></tr>
    </table>
    <p class="note">Note: Record defects before release. Reworked items must be re-inspected and approved prior to packing / dispatch.</p>
    </div>
  </section>`;

  // ── Page 3+: QC photo sections ─────────────────────────────────────────────
  // Only sections that have photos — empty sections would just create near-blank pages
  const sectionsWithContent = (report.sections || []).filter(sec => (sec.images || []).length);
  const fig = im => `
    <figure class="qimg">
      <div class="qframe"><img src="${esc(buildImageUrl(im.image_path))}" alt="" /></div>
      ${im.corner_note ? `<span class="corner">${esc(im.corner_note)}</span>` : ''}
      ${im.caption ? `<figcaption>${esc(im.caption)}</figcaption>` : ''}
    </figure>`;
  const kvTable = rows => `<table class="kv">${rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</table>`;
  const panel = (title, body) => `<div class="pn"><div class="pn-h">${title}</div><div class="pn-b">${body}</div></div>`;

  const sectionHtml = (sec, i) => {
    const list = sec.images || [];
    const first = list.slice(0, 3);
    const rest = list.slice(3);
    const isDrawing = /drawing/i.test(sec.section_name || '');
    const guide = pointsFor(sec);   // typed on the report page (standard list if never edited)
    const captions = list.map(im => im.caption).filter(has);

    const panels = [];
    if (isDrawing) {
      const details = [
        ['Proto Code', report.item_proto_code], ['Product Name', report.item_name],
        ['Drawing / Rev.', report.drawing_revision], ['Finish Code', finishCode], ['MO No.', moNo],
      ].filter(([, v]) => has(v));
      if (details.length) panels.push(panel('Product Details', kvTable(details)));
    }
    if (guide.length) panels.push(panel(isDrawing ? 'Check Points' : 'What to Check',
      `<ul class="chk">${guide.map(g => `<li>${esc(g)}</li>`).join('')}</ul>`));
    const desc = [['View', sec.section_name], ['Photos', String(list.length)]];
    if (has(sec.description)) desc.push(['Remarks', sec.description]);
    if (captions.length) desc.push(['Photo Notes', captions.join(' · ')]);
    panels.push(panel('Image Description', kvTable(desc)));

    return `<div class="ps">
      <div class="keep">
        <h3 class="ps-title"><span class="ps-no">${i + 1}</span>${esc(sec.section_name)}<em>(${list.length} Photo${list.length === 1 ? '' : 's'})</em></h3>
        <div class="ps-body">
          <div class="ps-imgs n${first.length}">${first.map(fig).join('')}</div>
          <aside class="ps-side">${panels.join('')}</aside>
        </div>
      </div>
      ${rest.length ? `<div class="ps-more">${rest.map(fig).join('')}</div>` : ''}
    </div>`;
  };

  const imagesHtml = !allImages.length ? '' : `
  <section class="photos">
    ${runHeads}
    ${bar(4, 'Inspection Photographs', ' ph-title')}
    ${sectionsWithContent.map(sectionHtml).join('')}
  </section>`;

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>${esc(report.report_no || 'QC Report')}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Montserrat:wght@500;600;700;800&display=swap" rel="stylesheet">
  <script>window.PagedConfig = { auto: false };<\/script>
  <script src="https://unpkg.com/pagedjs@0.4.3/dist/paged.polyfill.js"><\/script>
  <style>
    @page {
      size: A4; margin: 33mm 13mm 16mm 13mm;
      @top-left { content: element(runBrand); vertical-align: bottom; padding-bottom: 3mm; }
      @top-right { content: element(runDoc); vertical-align: bottom; padding-bottom: 3mm; }
      @bottom-left { content: "${cssStr(FOOTER_TAGLINE.toUpperCase())}"; font: 600 6.3pt Montserrat, Inter, Arial, sans-serif; letter-spacing: .18em; color: ${THEME.muted}; }
      @bottom-right { content: counter(page, decimal-leading-zero) " / " counter(pages, decimal-leading-zero); font: 700 8pt Montserrat, Inter, Arial, sans-serif; color: ${THEME.ink}; }
    }
    @page cover { margin: 0; @top-left { content: none; } @top-right { content: none; } @bottom-left { content: none; } @bottom-right { content: none; } }
    * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Inter, Arial, sans-serif; color: ${THEME.ink}; font-size: 8.8pt; line-height: 1.35; }

    /* ── Page decoration (pages 2+): warm paper, soft light shapes ── */
    .pagedjs_page:not(.pagedjs_cover_page) .pagedjs_pagebox { position: relative; z-index: 0; background: ${THEME.paper}; }
    .pagedjs_page:not(.pagedjs_cover_page) .pagedjs_pagebox::before { content: ""; position: absolute; inset: 0; z-index: -1; pointer-events: none;
      background:
        linear-gradient(118deg, transparent 0 58%, rgba(176,138,79,.07) 58% 71%, transparent 71%),
        linear-gradient(32deg, rgba(176,138,79,.07) 0 14%, transparent 14%); }
    /* Running header */
    .runBrand { position: running(runBrand); }
    .rb-word { font: 800 21pt/1 Montserrat, Inter, sans-serif; letter-spacing: -.01em; color: ${THEME.logo}; }
    .rb-tag { font: 600 5.6pt Montserrat, Inter, sans-serif; letter-spacing: .3em; text-transform: uppercase; color: ${THEME.muted}; margin-top: 1.4mm; }
    .runDoc { position: running(runDoc); text-align: right; font: 500 7.4pt Inter, sans-serif; color: ${THEME.ink}; line-height: 1.35; }
    .runDoc b { display: block; font: 700 8.4pt Montserrat, Inter, sans-serif; letter-spacing: .02em; }

    /* ── Cover ── */
    .cover { page: cover; position: relative; width: 210mm; height: 296.6mm; overflow: hidden; background: ${THEME.soft}; break-after: page; }
    .cv-art { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
    .cv-fallback { display: none; }
    .cover.no-art .cv-fallback { display: block; position: absolute; left: 22mm; right: 22mm; top: 70mm; text-align: center; }
    .cvf-co { font: 600 11pt Montserrat, Inter, sans-serif; letter-spacing: .32em; text-transform: uppercase; color: ${THEME.ink}; }
    .cvf-title { font: 800 46pt/1 Montserrat, Inter, sans-serif; letter-spacing: .12em; text-transform: uppercase; color: ${THEME.walnut}; margin-top: 18mm; }
    .cvf-title b { color: ${THEME.ink}; }
    .cvf-sub { font: 500 10pt Montserrat, Inter, sans-serif; letter-spacing: .3em; text-transform: uppercase; color: ${THEME.muted}; margin-top: 8mm; }
    /* Open area of the artwork: x 115–205mm, y ~134–160mm */
    .cv-plate { position: absolute; left: 120mm; right: 10mm; top: 135mm; padding: 1mm 3.5mm 1mm;
      background: transparent; border-left: .6mm solid ${THEME.gold}; }
    .cv-row { display: flex; gap: 2.5mm; align-items: baseline; padding: .55mm 0; }
    .cv-k { flex: 0 0 18mm; font: 700 6.2pt Montserrat, Inter, sans-serif; letter-spacing: .16em; text-transform: uppercase; color: ${THEME.gold}; }
    .cv-v { flex: 1; min-width: 0; font: 600 8pt Inter, sans-serif; color: ${THEME.ink}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    /* Long project names go to a second line (cut only after two lines) */
    .cv-v.cv-wrap { white-space: normal; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

    /* ── Section bars: tan band + walnut number box ── */
    .bar { display: flex; align-items: stretch; background: ${THEME.tan}; margin: 4mm 0 0; break-after: avoid; min-height: 7.2mm; }
    .bar-no { display: flex; align-items: center; justify-content: center; min-width: 10mm; padding: 0 2mm; background: ${THEME.walnut}; color: #fff;
      font: 700 9.5pt Montserrat, Inter, sans-serif; letter-spacing: .04em; }
    .bar-t { display: flex; align-items: center; padding: 0 3.5mm; font: 700 8.6pt Montserrat, Inter, sans-serif; letter-spacing: .1em; text-transform: uppercase; color: ${THEME.ink}; }

    /* ── Form ── */
    .form.has-next { break-after: page; }
    .fh { padding: 0 0 1mm; }
    .fh-k { font: 600 7pt Montserrat, Inter, sans-serif; letter-spacing: .26em; text-transform: uppercase; color: ${THEME.muted}; }
    .fh-t { font: 700 14pt/1.25 Montserrat, Inter, sans-serif; letter-spacing: .16em; text-transform: uppercase; color: ${THEME.ink}; margin: .6mm 0 .4mm; }
    table { width: 100%; border-collapse: collapse; background: #fff; }
    .info th, .info td, .disp td { border: .3mm solid ${THEME.line}; padding: 1.6mm 2.6mm; vertical-align: middle; font-size: 8.4pt; text-align: left; }
    .info th { width: 19%; background: ${THEME.paper}; font-weight: 600; color: #4a4640; }
    .info td { width: 31%; }
    .info .qty td { padding: 1.7mm 2.6mm; }
    .qrow { display: flex; justify-content: space-between; gap: 3mm; white-space: nowrap; }
    .fk { color: #4a4640; font-weight: 600; white-space: nowrap; }
    .fv { font-weight: 700; color: ${THEME.ink}; }
    .blank { display: inline-block; min-width: 12mm; border-bottom: .3mm solid #b3aa9e; height: 3.2mm; vertical-align: baseline; }
    .blank.wide { min-width: 30mm; } .blank.xwide { min-width: 120mm; }
    .check th { background: ${THEME.soft}; font: 700 7pt Montserrat, Inter, sans-serif; letter-spacing: .04em; text-align: left;
      padding: 1.7mm 2mm; border: .3mm solid ${THEME.line}; color: ${THEME.ink}; }
    .check td { border: .3mm solid ${THEME.line}; padding: 1.15mm 2mm; font-size: 8pt; }
    .check tr { break-inside: avoid; }
    .check .ip { font-weight: 600; }
    .check .c, .check th.c { text-align: center; width: 10.5mm; }
    .check .sn { width: 10mm; color: ${THEME.muted}; }
    .check .rm { width: 30mm; font-size: 7.4pt; color: #4a4640; }
    .cb { display: inline-flex; width: 3.9mm; height: 3.9mm; border: .35mm solid #b9b0a4; border-radius: .6mm; align-items: center; justify-content: center; vertical-align: middle; background: #fff; }
    .cb svg { width: 3.3mm; height: 3.3mm; }
    .cb-pass { border-color: ${THEME.pass}; background: ${THEME.pass}; }
    .cb-fail { border-color: ${THEME.fail}; background: ${THEME.fail}; }
    .cb-na { border-color: ${THEME.na}; background: ${THEME.na}; }
    .cb-rework { border-color: ${THEME.walnut}; background: ${THEME.walnut}; }
    .tally { display: flex; gap: 6mm; justify-content: flex-end; font-size: 8pt; color: ${THEME.muted}; padding: 2mm 1mm 0; }
    .tally span { display: inline-flex; align-items: center; gap: 1.4mm; } .tally b { color: ${THEME.ink}; }
    .opt { display: inline-flex; align-items: center; gap: 1.5mm; margin-left: 6mm; font-weight: 600; color: #4b4741; text-transform: uppercase; font-size: 7.8pt; letter-spacing: .03em; }
    .opt-on { color: ${THEME.ink}; } .opt-approved { color: ${THEME.pass}; } .opt-rejected { color: ${THEME.fail}; } .opt-rework { color: ${THEME.walnut}; }
    .remarks { margin-left: 2mm; white-space: pre-wrap; font-weight: 500; }
    .disp td { width: 100%; padding: 2.2mm 2.6mm; }
    .signs { display: flex; justify-content: space-between; gap: 4mm; flex-wrap: wrap; }
    .note { font-size: 7pt; color: ${THEME.muted}; margin-top: 2mm; }
    .keep { break-inside: avoid; }

    /* ── Photo sections: images left, info panels right ── */
    .ph-title { margin-top: 0; }
    .ps { margin-top: 4.5mm; break-inside: avoid; }   /* a section stays on one page unless it is taller than a page */
    .ps-title { display: flex; align-items: center; gap: 2.5mm; font: 700 8.6pt Montserrat, Inter, sans-serif; letter-spacing: .08em; text-transform: uppercase; color: ${THEME.ink};
      padding-bottom: 1.6mm; border-bottom: .35mm solid ${THEME.rust}; break-after: avoid; }
    .ps-no { display: inline-flex; align-items: center; justify-content: center; width: 5.4mm; height: 5.4mm; background: ${THEME.rust}; color: #fff; font-size: 8pt; letter-spacing: 0; }
    .ps-title em { font: 500 7.2pt Inter, sans-serif; font-style: normal; letter-spacing: 0; text-transform: none; color: ${THEME.muted}; }
    .ps-body { display: flex; gap: 3.5mm; padding-top: 3mm; align-items: flex-start; }
    .ps-imgs { flex: 1; min-width: 0; display: grid; gap: 2.5mm; }
    .ps-imgs.n1 { grid-template-columns: 1fr; } .ps-imgs.n2 { grid-template-columns: 1fr 1fr; } .ps-imgs.n3 { grid-template-columns: 1fr 1fr 1fr; }
    .ps-imgs.n1 .qframe { height: 74mm; } .ps-imgs.n2 .qframe { height: 60mm; } .ps-imgs.n3 .qframe { height: 50mm; }
    .ps-side { flex: 0 0 64mm; display: flex; flex-direction: column; gap: 2.5mm; }
    .ps-more { display: grid; grid-template-columns: repeat(4, 1fr); gap: 2.5mm; padding-top: 2.5mm; }
    .ps-more .qframe { height: 40mm; }
    .qimg { break-inside: avoid; position: relative; border: .3mm solid ${THEME.line}; background: #fff; }
    .qframe { background: ${THEME.soft}; display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .qframe img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
    .qframe .missing { font-size: 7.5pt; color: ${THEME.muted}; }
    .qimg .corner { position: absolute; top: 1.5mm; right: 1.5mm; background: ${THEME.walnut}; color: #fff; font-size: 6.8pt; font-weight: 700; padding: .5mm 2mm; border-radius: 3mm; }
    .qimg figcaption { font-size: 7pt; padding: 1.2mm 1.8mm; color: #4a4640; border-top: .3mm solid ${THEME.line}; }
    .pn { border: .3mm solid ${THEME.line}; background: #fff; break-inside: avoid; }
    .pn-h { background: ${THEME.tan}; font: 700 7pt Montserrat, Inter, sans-serif; letter-spacing: .12em; text-transform: uppercase; color: ${THEME.walnut}; padding: 1.5mm 2.4mm; }
    .pn-b { padding: 1.6mm 2.4mm; }
    .chk { list-style: none; display: grid; gap: 1mm; }
    .chk li { position: relative; padding-left: 4.4mm; font-size: 7.6pt; color: #3d3a35; }
    .chk li::before { content: ""; position: absolute; left: 0; top: .35mm; width: 3.1mm; height: 3.1mm; border-radius: 50%;
      background: ${THEME.gold} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M4 8.3l2.6 2.6L12 5.4' fill='none' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / 80% no-repeat; }
    .kv { background: transparent; }
    .kv th, .kv td { font-size: 7.4pt; padding: .9mm 0; vertical-align: top; text-align: left; border-bottom: .25mm solid ${THEME.line}; }
    .kv tr:last-child th, .kv tr:last-child td { border-bottom: 0; }
    .kv th { width: 22mm; font-weight: 600; color: ${THEME.muted}; padding-right: 2mm; }
    .kv td { color: ${THEME.ink}; font-weight: 500; word-break: break-word; }

    @media screen { body { background: #e7e2da; } .pagedjs_page { margin: 0 auto 8mm; box-shadow: 0 2px 10px rgba(0,0,0,.15); } }
  </style></head><body>
    ${cover}
    ${form}
    ${imagesHtml}
    <script>
      (function () {
        // Broken/blocked images: cover falls back to a text title, grid shows a clean note
        function broken(im) {
          if (!im.parentNode) return;
          if (im.getAttribute('data-cover')) { im.parentNode.classList.add('no-art'); im.remove(); return; }
          if (im.parentNode.classList.contains('qframe')) {
            var m = document.createElement('span'); m.className = 'missing'; m.textContent = 'Image unavailable';
            im.parentNode.replaceChild(m, im);
          }
        }
        function imagesReady() {
          var imgs = Array.prototype.slice.call(document.images);
          return Promise.all(imgs.map(function (im) {
            if (im.complete) { if (!im.naturalWidth) broken(im); return Promise.resolve(); }   // already failed before this ran
            return new Promise(function (r) {
              im.addEventListener('load', r);
              im.addEventListener('error', function () { broken(im); r(); });
            });
          }));
        }
        function timeout(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
        window.addEventListener('load', function () {
          var fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
          Promise.race([Promise.all([imagesReady(), fonts]), timeout(12000)]).then(function () {
            if (!window.PagedPolyfill) { window.print(); return; }   // offline fallback: plain print
            return window.PagedPolyfill.preview().then(function () {
              setTimeout(function () { window.print(); }, 400);
            });
          }).catch(function () { window.print(); });
        });
      })();
    <\/script>
  </body></html>`;
}
